import PublicApiService from "../../../../../services/PublicApiService";
import { DeviceInfo, DeviceSetupQrConfig } from "../../types";
import { DeviceActivationPayload } from "../../../../../services/types";
import { v4 as uuid } from "uuid";
import { DeviceType } from "../../../../../common/enums";
import DeviceCacheHelper from "../deviceCacheHelper/DeviceCacheHelper";
import utils from "../../../../../common/utils/hashUtils";
import { filterByValidDeviceName, sortDevices } from "../../sortDevices/sortDevices";

class DeviceActions {
  private _newDeviceQrCode = "";
  private _newDeviceName = "";
  private _deviceSetupConfig?: DeviceSetupQrConfig;
  private readonly SETUP_QR_PARTS_COUNT = 7;

  get newDeviceName() {
    return this._newDeviceName;
  }

  get newDeviceQrCode(): string {
    return this._newDeviceQrCode;
  }

  get deviceSetupConfig(): DeviceSetupQrConfig | undefined {
    return this._deviceSetupConfig;
  }

  async addNewDevice(eventEditionGbsCode: string, deviceName: string): Promise<void | never> {
    this._newDeviceName = "";
    if (!deviceName) {
      return;
    }
    this._newDeviceName = deviceName;
    const apiResponse = await PublicApiService.generateQrCodeForDevice(eventEditionGbsCode, deviceName);
    this._newDeviceQrCode = apiResponse.qrCode;
  }

  resetDeviceQrCode() {
    this._newDeviceQrCode = "";
    this._newDeviceName = "";
  }

  isQrCodeGenerated() {
    return Boolean(this.newDeviceQrCode);
  }

  resetDeviceSetupConfig() {
    this._deviceSetupConfig = undefined;
  }

  decodeDeviceSetupQr(qrContent: string) {
    const splitted = qrContent.split("|");

    if (splitted.length !== this.SETUP_QR_PARTS_COUNT) {
      return;
    }

    this._deviceSetupConfig = {
      deviceInstanceId: splitted[1]!,
      name: splitted[2]!,
      challenge: splitted[3]!,
      part1HmacSecret: splitted[4]!,
      eventEditionGbsCode: splitted[5]!,
      eventEditionId: splitted[6]!
    };
  }

  async activateDevice() {
    const { challenge, eventEditionGbsCode, part1HmacSecret } = this.deviceSetupConfig!;
    const part2hmac = uuid().replaceAll("-", "");
    const payload: DeviceActivationPayload = {
      challenge,
      eventEditionGbsCode,
      partTwoHmacSecret: part2hmac,
      type: DeviceType.KIOSK
    };
    await PublicApiService.activateDeviceQrCode(this.deviceSetupConfig!.deviceInstanceId, payload);
    DeviceCacheHelper.saveDeviceHmac(part1HmacSecret, part2hmac);
  }

  filterDevices(devices: DeviceInfo[]) {
    try {
      const result = devices.filter(filterByValidDeviceName);
      return result.sort(sortDevices);
    } catch (e) {
      return [];
    }
  }

  async getDeviceHmac(value: string, { handleUrl } = { handleUrl: false }) {
    const hmacFromCache = await DeviceCacheHelper.getDeviceHmac();
    const valueToHash = handleUrl ? new URL(value).search.replace("?", "") : value;
    return utils.computeHash(valueToHash, hmacFromCache);
  }

  async getDeviceHmacFromBody(json: string) {
    const hmacFromCache = await DeviceCacheHelper.getDeviceHmac();
    return utils.computeHash(json, hmacFromCache);
  }
}

export default new DeviceActions();
